import Viz from '../../../static/plugins/graph/index'

// Lays out Graphviz DOT in a Web Worker, so drawing a big journey (which can take many seconds)
// doesn't freeze the page. Falls back to the main thread if the worker can't start.
//
// - Only the newest request matters: asking again while a layout is still running stops the old one.
// - Recent results are cached, so redrawing an unchanged graph (fit to screen, toggles back) is instant.

const WORKER_URL = '/static/plugins/graph/worker.js'
const CACHE_SIZE = 6

const cache = new Map()
let worker = null
let workerBroken = false
let running = null // { id, dot, resolve, reject }
let lastId = 0

function remember (dot, svg) {
  cache.delete(dot)
  cache.set(dot, svg)
  if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value)
}

function layoutOnMainThread (dot) {
  // Same larger heap as the worker; the default 16MB runs out at a few hundred stages
  const svg = Viz(dot, { format: 'svg', totalMemory: 256 * 1024 * 1024 })
  remember(dot, svg)
  return svg
}

function stopWorker () {
  if (worker) worker.terminate()
  worker = null
}

function startWorker () {
  worker = new Worker(WORKER_URL)
  worker.onmessage = event => {
    const job = running
    if (!job || event.data.id !== job.id) return
    running = null
    if (event.data.error) {
      job.reject(new Error(event.data.error))
    } else {
      remember(job.dot, event.data.svg)
      job.resolve(event.data.svg)
    }
  }
  // The script didn't load (or crashed): use the main thread from now on
  worker.onerror = event => {
    event.preventDefault()
    workerBroken = true
    stopWorker()
    const job = running
    running = null
    if (!job) return
    try {
      job.resolve(layoutOnMainThread(job.dot))
    } catch (error) {
      job.reject(error)
    }
  }
}

export function layoutGraph (dot) {
  if (cache.has(dot)) {
    const svg = cache.get(dot)
    remember(dot, svg)
    return Promise.resolve(svg)
  }
  if (workerBroken || typeof Worker === 'undefined') {
    try {
      return Promise.resolve(layoutOnMainThread(dot))
    } catch (error) {
      return Promise.reject(error)
    }
  }
  // A layout is still running for an older graph: nobody wants it any more
  if (running) {
    const old = running
    running = null
    stopWorker()
    old.reject(Object.assign(new Error('superseded'), { superseded: true }))
  }
  return new Promise((resolve, reject) => {
    try {
      if (!worker) startWorker()
    } catch (error) {
      workerBroken = true
      try {
        resolve(layoutOnMainThread(dot))
      } catch (mainError) {
        reject(mainError)
      }
      return
    }
    running = { id: ++lastId, dot, resolve, reject }
    worker.postMessage({ id: running.id, dot })
  })
}



// WEBPACK FOOTER //
// ./src/components/shared/graphLayout.js